import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { IconButton, Modal } from "rsuite";
import TrashIcon from "@rsuite/icons/Trash";
import { DataTable, useEditableRow, EditableRowActions, EditableTableCell, FIELD_TYPES, Button } from "../../../components";
import CentresService from "../centres.service";
import { useAuthenticationService } from "../../../services/authentication.service";
import { Eventbus } from "../../../utils"
import { roles, events } from "../../../utils/constants";

import ToastService from "../../../services/toast.service";
import { FaChildren } from "react-icons/fa6";
import { FaSync } from "react-icons/fa";
import "./centresTable.css";

// Field configuration for the centres table
const FIELD_CONFIG = {
  swnNumber: {
    type: FIELD_TYPES.TEXT,
    regex: "\\d", // Only allow digits
    maxLength: 11,
    placeholder: "000-000-000",
    format: (val) => {
      const numbers = val.replace(/\D/g, "").slice(0, 9);
      let formatted = "";
      for (let i = 0; i < numbers.length; i++) {
        if (i > 0 && i % 3 === 0) {
          formatted += "-";
        }
        formatted += numbers[i];
      }
      return formatted;
    },
  },
  centreName: { type: FIELD_TYPES.TEXT },
  eceNumber: { type: FIELD_TYPES.TEXT },
  costCodeAlpha: { type: FIELD_TYPES.TEXT, format: (val) => val.toUpperCase() },
};

function CentresTable({
  data,
  sortColumn,
  sortType,
  onSortColumn,
  loading,
  activeFilters,
  onFilterClick,
  // Pagination props
  currentPage,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
}) {
  const navigate = useNavigate();
  const AuthenticationService = useAuthenticationService();
  const isAdmin = AuthenticationService.isInAnyRole(roles.ADMINS);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [centreToDelete, setCentreToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // Track sync status for each eceNumber
  const [syncStatus, setSyncStatus] = useState({});

  // Use the editable row hook
  const { editingRowId, editingData, saving, startEditing, cancelEditing, handleFieldChange, saveRow, isEditing } = useEditableRow({
    onSave: handleSaveCentre,
    onError: (error) => {
      ToastService.error(`Failed to update centre: ${error.message || "Unknown error"}`);
    },
  });

  async function handleSaveCentre(id, updatedData) {
    const centreId = id.centreId || id;
    await CentresService.updateCentreRecord(centreId, updatedData);
    ToastService.success("Centre updated successfully");
  }

  const handleDeleteCentre = (centre) => {
    setCentreToDelete(centre);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!centreToDelete) return;

    setDeleting(true);
    try {
      await CentresService.deleteCentre(centreToDelete.id.centreId || centreToDelete.id);
      ToastService.success("Centre deleted successfully");
      setDeleteModalOpen(false);
      setCentreToDelete(null);
    } catch (error) {
      ToastService.error(`Failed to delete centre: ${error.message || "Unknown error"}`);
    } finally {
      setDeleting(false);
    }
  };

  function startSync(centreId) {
    CentresService.syncWinzCentre(centreId)
      .then(() => {
        ToastService.success("Centre sync initiated successfully", "centre-sync");
      })
      .catch((error) => {
        ToastService.error(`Failed to sync centre: ${error.message || "Unknown error"}`);
      });
  }

  const cancelDelete = () => {
    setDeleteModalOpen(false);
    setCentreToDelete(null);
  };
  function loadchildMatches(id) {
    // Extract centreId from the id object if it exists
    const centreId = typeof id === "object" && id.centreId ? id.centreId : id;
    // Navigate to the child match page
    navigate(`/centres/${centreId}`);
  }
  const columns = [
    {
      key: "actions",
      title: "Actions",
      width: 190,
      sortable: false,
      filterable: false,
      render: (rowData) => {
        // Use eceNumber as key for sync status, fallback to id
        const eceKey = rowData.eceNumber || rowData.id;
        const isSyncing = syncStatus[eceKey] === 'Started';
        return (
          <div className="action-buttons" style={{ display: "flex", gap: "4px", alignItems: "center" }}>
            { isAdmin && (
            <EditableRowActions
              rowData={rowData}
              isEditing={isEditing(rowData.id?.centreId || rowData.id)}
              onEdit={startEditing}
              onSave={saveRow}
              onCancel={cancelEditing}
              saving={saving}
            />
            )}
            <IconButton
              icon={<FaChildren />}
              size="xs"
              appearance="subtle"
              onClick={() => {
                loadchildMatches(rowData.id);
              }}
              title="View Child Match"
              className="child-match-button"
              style={{ display: isEditing(rowData.id) ? "none" : "block" }}
            />
            { isAdmin && (
            <IconButton
              icon={<FaSync />}
              size="xs"
              appearance="subtle"
              onClick={() => startSync(rowData.eceNumber || rowData.id)}
              title="Sync Centre"
              className={`sync-button ${isSyncing ? 'syncing' : ''}`}
              style={{ display: isEditing(rowData.eceNumber || rowData.id) ? "none" : "block" }}
              disabled={isSyncing}
            />
            )}
            { isAdmin && (
            <IconButton
              icon={<TrashIcon />}
              size="xs"
              appearance="subtle"
              onClick={() => handleDeleteCentre(rowData)}
              title="Delete Centre"
              className="delete-button"
              style={{ display: isEditing(rowData.id) ? "none" : "block" }}
            />
            )}
          </div>
        );
      },
    },
    {
      key: "swnNumber",
      dataKey: "swnNumber",
      title: "SWN Number",
      width: 200,
      sortable: true,
      filterable: true,
      render: (rowData) => (
        <EditableTableCell
          rowData={rowData}
          fieldKey="swnNumber"
          fieldConfig={FIELD_CONFIG.swnNumber}
          editingRowId={editingRowId}
          editingData={editingData}
          onFieldChange={handleFieldChange}
        />
      ),
    },
    {
      key: "centreName",
      dataKey: "centreName",
      title: "Centre Name",
      flexGrow: 2,
      sortable: true,
      filterable: true,
      render: (rowData) => (
        <EditableTableCell
          rowData={rowData}
          fieldKey="centreName"
          fieldConfig={FIELD_CONFIG.centreName}
          editingRowId={editingRowId}
          editingData={editingData}
          onFieldChange={handleFieldChange}
        />
      ),
    },
    {
      key: "eceNumber",
      dataKey: "eceNumber",
      title: "ECE Number",
      width: 150,
      sortable: true,
      filterable: true,
      render: (rowData) => (
        <EditableTableCell
          rowData={rowData}
          fieldKey="eceNumber"
          fieldConfig={FIELD_CONFIG.eceNumber}
          editingRowId={editingRowId}
          editingData={editingData}
          onFieldChange={handleFieldChange}
        />
      ),
    },
     {
      key: "costCodeAlpha",
      dataKey: "costCodeAlpha",
      title: "Cost Code Alpha",
      width: 150,
      sortable: true,
      filterable: true,
      render: (rowData) => (
        <EditableTableCell
          rowData={rowData}
          fieldKey="costCodeAlpha"
          fieldConfig={FIELD_CONFIG.costCodeAlpha}
          editingRowId={editingRowId}
          editingData={editingData}
          onFieldChange={handleFieldChange}
        />
      ),
    },
  ];

  // Update sync status for a centre when event received
  const updateSyncStatus = useCallback((msg) => {
    // msg: { status: 'started'|'stopped', eceNumber: '...' }
    if (msg && msg.eceNumber) {
      setSyncStatus(prev => ({ ...prev, [msg.eceNumber]: msg.status }));
    }
  }, []);

  useEffect(() => {
    Eventbus.on(events.SIGNALR.WINZ_CENTRE_DATA_SYNC, updateSyncStatus);
    return () => {
      Eventbus.off(updateSyncStatus);
    };
  }, [updateSyncStatus]);

  return (
    <>
      <DataTable
        data={data}
        columns={columns}
        sortColumn={sortColumn}
        sortType={sortType}
        onSortColumn={onSortColumn}
        loading={loading}
        activeFilters={activeFilters}
        onFilterClick={onFilterClick}
        expandable={false}
        showRowCount={true}
        currentPage={currentPage}
        pageSize={pageSize}
        totalItems={totalItems}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
      <Modal open={deleteModalOpen} onClose={cancelDelete} size="sm">
        <Modal.Header>
          <Modal.Title>Delete Centre</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the centre "{centreToDelete?.centreName}"?
          <br />
          This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={confirmDelete} appearance="primary" loading={deleting} disabled={deleting} text="Delete" color="red" data-testid="confirm-delete-button" />
          <Button onClick={cancelDelete} appearance="primary" disabled={deleting} text="Cancel" color="blue" data-testid="cancel-delete-button" />
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default CentresTable;
